import React from 'react';
import { Checkbox, Stack, Loader, Shelf, Position } from '@chaser/components';
import useFetch, { useCacheActions } from '../../../../../hooks/useFetch';
import SettingsService from '../../../../../services/settings-service';
import usePush from '../../../../../hooks/usePush';
import useShortCode from '../../../../../hooks/useShortCode';
import OrganisationService from '../../../../../services/organisation-service';
import InfoTooltip from '../../../../../components/InfoTooltip';
import styles from './CustomInvoicePDFs.module.scss';

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

const CustomInvoicePDFs = () => {
  const shortCode = useShortCode();
  const { invalidateEndpoint } = useCacheActions();
  const { data, applyOptimisticUpdate } = useFetch(
    SettingsService.getIntegrationFeatures,
    shortCode,
  );
  const { execute: executeCustomInvoicePdf, loading: customInvoicePdfLoading } = usePush(
    SettingsService.setCustomInvoicePdf,
  );
  const { execute: executeHideIntercompanyDocs, loading: hideIntercompanyDocsLoading } = usePush(
    async (currentShortCode, enabled) => {
      const promise = SettingsService.setHideIntercompanyDocs(currentShortCode, enabled);

      applyOptimisticUpdate(state => {
        state.hide_intercompany_docs = enabled;
      }, promise);

      await promise;

      await OrganisationService.resetLastRefreshDate(currentShortCode);
      await Promise.all([OrganisationService.sync(currentShortCode), wait(1500)]);
      invalidateEndpoint(OrganisationService.get);
    },
  );
  const loading = customInvoicePdfLoading || hideIntercompanyDocsLoading;

  return (
    <div className={styles.container}>
      <Stack gap="small" className={loading ? styles.contentBlur : undefined}>
        <Checkbox
          name="custom-invoice-pdf"
          checked={data.custom_invoice_pdf}
          disabled={loading}
          onChange={async e => {
            const { checked } = e.target;
            applyOptimisticUpdate(state => {
              state.custom_invoice_pdf = checked;
            }, executeCustomInvoicePdf(shortCode, checked));
          }}
          data-trackingid="custom-invoice-pdf"
        >
          {/* this option is also present on welcome screen */}
          Replace the default AccountsIQ invoice PDF with a custom attachment
        </Checkbox>
        <Checkbox
          name="hide-intercompany-docs"
          checked={!!data.hide_intercompany_docs}
          disabled={loading}
          onChange={async e => {
            const { checked } = e.target;
            executeHideIntercompanyDocs(shortCode, checked);
          }}
          data-trackingid="hide-intercompany-docs"
        >
          <Shelf gap="xsmall" alignItems="center">
            Hide intercompany documents
            <InfoTooltip iconSize={14} position={Position.RIGHT}>
              Intercompany contacts are AccountsIQ contacts that represent companies within your own
              group (for example, related entities or subsidiaries). Enable this to hide those
              documents in Chaser.
            </InfoTooltip>
          </Shelf>
        </Checkbox>
      </Stack>
      {loading && (
        <div className={styles.overlay}>
          <Loader size={16} />
          <span className={styles.syncLabel}>Syncing...</span>
        </div>
      )}
    </div>
  );
};

export default CustomInvoicePDFs;
